import React, { useMemo } from 'react';
import { Loader2 } from 'lucide-react';
import { Ico } from '../ui/Icons';
import { C } from '../../styles/tokens';
import { useAIVoicesStore } from '../../zustand-store/useAIVoicesStore';
import { usePreGenAudioPlayer } from '../../zustand-store/usePreGenAudioPlayerStore';
import { withMp3Extension } from '../../utils/voicePreview';

const GRADIENTS = [
    'linear-gradient(135deg,#667eea,#764ba2)',
    'linear-gradient(135deg,#f093fb,#f5576c)',
    'linear-gradient(135deg,#4facfe,#00f2fe)',
    'linear-gradient(135deg,#43e97b,#38f9d7)',
    'linear-gradient(135deg,#fa709a,#fee140)',
    'linear-gradient(135deg,#a18cd1,#fbc2eb)',
];

const ENGINE_LABEL = { emotional: 'Emotional', neural: 'Premium', standard: 'Standard' };

function sortByPriority(voices) {
    const pri = { emotional: 3, neural: 2, standard: 1 };
    return [...voices].sort((a, b) => {
        const ed = (pri[b.engine] || 0) - (pri[a.engine] || 0);
        if (ed !== 0) return ed;
        const sd = b.styles.length - a.styles.length;
        if (sd !== 0) return sd;
        return a.name.localeCompare(b.name);
    });
}

function VoiceAvatar({ name, gradient }) {
    return (
        <div style={{
            width: 44, height: 44, borderRadius: '50%', flexShrink: 0,
            background: gradient,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            fontSize: 18, fontWeight: 700, color: '#fff',
        }}>
            {name[0]}
        </div>
    );
}

function VoiceCard({ voice, gradient }) {
    const instanceKey = `trending:${voice.code}`;
    const { activeKey, isPlaying, loadingSrc, togglePlayPause } = usePreGenAudioPlayer();
    const audioURL = `${process.env.REACT_APP_VOICE_PREVIEW_CDN}/${withMp3Extension(voice.audio)}`;
    const active = isPlaying && activeKey === instanceKey;
    const isLoadingAudio = activeKey === instanceKey && !!loadingSrc;

    const engineLabel = ENGINE_LABEL[voice.engine] || voice.engine;
    const genderLabel = voice.gender ? voice.gender.charAt(0).toUpperCase() + voice.gender.slice(1) : '';

    return (
        <div
            style={{
                display: 'flex', alignItems: 'center', gap: 12,
                padding: '12px 14px',
                border: `1px solid ${C.n100}`,
                borderRadius: 12,
                background: C.w,
                transition: 'box-shadow .15s',
            }}
            onMouseEnter={e => { e.currentTarget.style.boxShadow = '0 2px 12px rgba(0,0,0,.07)'; }}
            onMouseLeave={e => { e.currentTarget.style.boxShadow = 'none'; }}
        >
            <VoiceAvatar name={voice.name} gradient={gradient} />

            <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: 13, fontWeight: 600, color: C.n900, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                    {voice.name}
                </div>
                <div style={{ fontSize: 11.5, color: C.n400, marginTop: 2, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                    {engineLabel}{genderLabel ? ` · ${genderLabel}` : ''}
                </div>
            </div>

            <button
                onClick={() => !isLoadingAudio && togglePlayPause(audioURL, instanceKey)}
                disabled={isLoadingAudio}
                style={{
                    width: 32, height: 32, borderRadius: '50%', flexShrink: 0,
                    border: `1px solid ${active ? C.n900 : C.n200}`,
                    background: active ? C.n900 : C.w,
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    cursor: isLoadingAudio ? 'not-allowed' : 'pointer', transition: 'all .15s',
                }}
                title={isLoadingAudio ? 'Loading…' : active ? 'Pause' : 'Preview'}
            >
                {isLoadingAudio
                    ? <Loader2 size={13} color={active ? C.w : C.n700} className="vi-spin" />
                    : <Ico n={active ? 'pause' : 'play'} size={13} color={active ? C.w : C.n700} />
                }
            </button>
        </div>
    );
}

export function TrendingVoices() {
    const selectedLanguage = useAIVoicesStore(state => state.selectedLanguage);
    const languages = useAIVoicesStore(state => state.languages);

    const topVoices = useMemo(() => {
        const raw = selectedLanguage?.voices ?? languages?.['en-US']?.voices ?? [];
        return sortByPriority(raw).slice(0, 6);
    }, [selectedLanguage, languages]);

    if (topVoices.length === 0) return null;

    return (
        <div style={{ marginBottom: 28 }}>
            <p style={{ fontSize: 14, fontWeight: 600, color: C.n900, marginBottom: 12 }}>
                Trending voices
            </p>

            {/* Mobile: horizontal scroll row. Desktop (≥768px): 3-column grid via CSS class */}
            <div className="trending-voices-grid">
                {topVoices.map((voice, i) => (
                    <div key={voice.code} className="trending-voices-item">
                        <VoiceCard voice={voice} gradient={GRADIENTS[i % GRADIENTS.length]} />
                    </div>
                ))}
            </div>
        </div>
    );
}
